/**
 * What a purchase actually cost, as opposed to what the screen quoted.
 *
 * WHY THIS EXISTS
 * Every monthly entry stored the quoted price and nothing else, so the commission the broker
 * actually charged was never part of cost basis. Measured against the IBKR activity statement
 * for 2026-01-05..07-31: all thirteen US positions understated cost, by $1.91 to $4.93 each,
 * $39 in total — and growing every month, because the contribution grows. Cost is the
 * denominator of XIRR and of unrealized P&L, so every return the app printed was high by
 * construction. (audit 2026-08-03)
 *
 * THE RULE IS MEASURED, NOT ASSUMED
 * The obvious fix — "add 1%" — is wrong, and the data says so. Across all 89 fills in that
 * statement the charge is:
 *
 *     fee = min($1.00, 1% of trade value)          89/89 fills, zero deviation
 *
 * A flat 1% would overstate every purchase above $100: a $169 VOO buy is charged $1.00, not
 * $1.69. The error would grow with the monthly contribution — the same mistake this module
 * exists to correct, pointed the other way.
 *
 * AND IT IS PER BROKER, NOT GLOBAL. Four different charges sit behind one bank:
 *   - TEFAS funds: nothing, either side. The fund's own fee is inside the NAV, so charging on
 *     top would count it twice. Corroborated independently — the owner's bank debits for
 *     TEJ/AFA/YKT land within TRY 9 of budget x allocation across six months.
 *   - BIST equities: 0.195% brokerage, both sides.
 *   - Gram gold, silver and FX cash: no service commission — the dealer's margin is already
 *     inside the quoted buy price — but a statutory 0.2% kambiyo muamele vergisi is withheld.
 *     Two statements fix the rate exactly: TRY 5,599.82 -> 11.20 and TRY 6,636.92 -> 13.27.
 *
 * What is left UNKNOWN is crypto, which is not held at a bank and has never been checked
 * against an exchange statement. An unknown fee model is reported by the watchdog rather than
 * silently treated as zero, because a guess there is exactly the class of error the rest of
 * this module exists to undo.
 *
 * NOTHING IS BACKFILLED. These rates apply from the next purchase forward, by the owner's
 * instruction. The existing entries record PLAN amounts, and whether a plan amount was the
 * gross intent or the net debit is not recoverable from anything the app holds — only a bank
 * statement settles it, and the whole exposure is about TRY 350.
 *
 * NO CALCULATION DEPENDS ON THE FIELDS THIS PRODUCES.
 * `price` on an entry stays the single source of cost (cost = qty x price), and it is now the
 * NET price. `grossPrice` and `feeLocal` are recorded beside it as provenance, so a report can
 * show what was quoted and what was charged without any downstream sum having to learn a new
 * shape. Adding a second addend to a cost formula used in nine places is how the fee would
 * end up counted twice somewhere.
 */

/** A fee model that cannot be evaluated. Never silently zero. */
export const UNKNOWN = 'unknown';

/**
 * Per-broker charge, in the currency of the trade.
 *
 * `ibkr-fractional`: IBKR's fixed schedule for fractional US equity — $0.005/share, floor
 * $1.00, ceiling 1% of trade value. At the sizes here the floor and ceiling are the whole
 * story, and min($1.00, 1%) reproduces all 89 observed fills exactly.
 */
export const FEE_MODELS = {
  'ibkr-fractional': v => Math.min(1.00, 0.01 * v),
  // IBKR prices the LSE lines on a different schedule from fractional US equity. Seven buys on
  // 2026-08-05 (venues LSEETF, EUDARK, TRWBUKETF) were charged exactly USD 4.00 each, on trade
  // values from 433.95 to 1,675.18 — a 4x range that never moved the number.
  //
  // Flat 4.00 and "0.05% with a 4.00 minimum" both reproduce all seven fills exactly. The first
  // order that would separate them is above USD 8,000 and none is planned, so this is CALIBRATED,
  // not derived: right across every observed and planned size, and to be re-measured the day a
  // single order clears USD 8,000.
  'ibkr-lse': () => 4.00,
  // BIST equities at Garanti: 0.195% (binde 1.95) on both sides. Owner-supplied 2026-08-03.
  'bist-brokerage': v => 0.00195 * v,
  // Gram gold, silver, and FX cash at Garanti take no service commission — the dealer's
  // margin is already inside the quoted buy price and must not be charged twice. What IS
  // withheld is the statutory 0.2% kambiyo muamele vergisi. Owner-supplied 2026-08-03 from
  // two statements: TRY 5,599.82 -> 11.20 and TRY 6,636.92 -> 13.27, both 0.2000% exactly.
  'tr-bsmv': v => 0.002 * v,
  // Garanti BBVA Kripto: the 0.2% VAT-inclusive commission is REAL and it is ALREADY INSIDE
  // THE QUOTED PRICE, so nothing is added here. Same shape as `Fund` (the fee is inside the
  // NAV) and `xetra` (the plan statement carries its own cost) — the charge exists, it is just
  // not charged on top of the number the owner reads off the screen.
  //
  // ⚠ THIS MODEL CHARGED IT A SECOND TIME FOR FIVE WEEKS. Owner-supplied 2026-08-03 as "0.2%
  // each side", which is true about the world and was implemented as 0.2% ON TOP. Measured
  // 2026-09-11 against a single transfer, and the arithmetic is not close:
  //
  //     sent to Garanti BBVA Kripto                        TRY 373,242.00
  //     BTC/ETH/XRP at the quoted prices                   TRY 371,005.23
  //     fee ON TOP  -> spend 371,747.24 -> fiat left            USD 30.98
  //     fee INSIDE  -> spend 371,005.23 -> fiat left            USD 46.35
  //     the platform's own screen said                          USD 45.60
  //
  // 75 cents against 14.62 dollars, on a 373,242 lira transfer. The remaining 75 cents is the
  // rounding of six figures displayed to two decimals. Sell side confirmed all-in by the owner
  // the same day, which is why this is one number and not a buy-side special case.
  //
  // Kept as its own named model rather than routed to 'none': the rate is a coincidence, the
  // REASON is the fact, and a later change to one must not silently rewrite the other.
  'garanti-crypto': () => 0,
  'none': () => 0,
  [UNKNOWN]: () => null,
};

/**
 * Which model a holding's purchases are charged under.
 *
 * Resolved from the broker recorded on the holding, because that is the fact — not from the
 * asset type, which only correlates. A TEJ row that says 'Garanti + Akbank' is still a TEFAS
 * purchase; an ETF bought somewhere else would not be.
 */
export function feeModelFor(h) {
  const broker = String((h && h.broker) || '').toLowerCase();
  const type = (h && h.type) || '';
  if (!broker) return UNKNOWN;
  if (broker.includes('ibkr')) {
    // Routed on the '.L' suffix because the listing venue is what IBKR prices, and the ticker is
    // the only record of the venue a holding carries. A `venue` field would be the honest fact;
    // the suffix stands in for it until there is one.
    return /\.L$/i.test(String((h && h.ticker) || '')) ? 'ibkr-lse' : 'ibkr-fractional';
  }
  // Garanti covers four different things and they are charged four different ways.
  if (broker.includes('garanti') || broker.includes('akbank')) {
    if (type === 'Fund') return 'none';                 // TEFAS — no commission either side; the fund fee is inside the NAV
    if (type === 'Stock') return 'bist-brokerage';      // 0.195%
    // Metals and FX are charged the same way: no service commission, 0.2% transaction tax.
    if (type === 'Commodity' || type === 'Cash') return 'tr-bsmv';
    // Crypto sits on the bank's own venue (Garanti BBVA Kripto). Its 0.2% VAT-inclusive
    // commission is inside the quoted price on BOTH sides — see the model above, where the
    // measurement that settled it is recorded. This was the last UNKNOWN in the book; the
    // integrity watchdog carried it as F1 for exactly as long as it took to get a number
    // rather than assume one, and it took a second measurement to learn where the number sits.
    if (type === 'Crypto') return 'garanti-crypto';
    return UNKNOWN;
  }
  // The SAP plan's lots arrive with their own cost from the plan statement; nothing is
  // charged on top here.
  if (broker.includes('xetra')) return 'none';
  return UNKNOWN;
}

/**
 * The charge on one purchase, in the holding's own currency.
 * @returns {{feeLocal: number|null, model: string, known: boolean}}
 */
export function feeFor(h, valueLocal) {
  const model = feeModelFor(h);
  const fn = FEE_MODELS[model] || FEE_MODELS[UNKNOWN];
  const v = Number(valueLocal);
  if (!(v > 0)) return { feeLocal: 0, model, known: model !== UNKNOWN };
  const fee = fn(v);
  return { feeLocal: fee == null ? null : +fee.toFixed(6), model, known: fee != null };
}

/**
 * Turn a quoted purchase into the numbers an entry stores.
 *
 * An unknown model returns the quoted price untouched and says so, rather than assuming zero.
 * The difference matters: a zero fee is a measurement, an absent one is a gap, and the
 * watchdog has to be able to tell them apart.
 *
 * @returns {{qty:number, price:number, grossPrice:number, feeLocal:number|null, feeModel:string}}
 */
export function pricePurchase(h, qty, quotedPrice) {
  const q = Number(qty) || 0;
  const p = Number(quotedPrice) || 0;
  const gross = q * p;
  const { feeLocal, model } = feeFor(h, gross);
  const net = feeLocal == null ? gross : gross + feeLocal;
  return {
    qty: q,
    price: q > 0 ? net / q : 0,
    grossPrice: p,
    feeLocal: feeLocal,
    feeModel: model,
  };
}

/**
 * Turn a quoted sale into the numbers an exit stores.
 *
 * The mirror of pricePurchase, and it was missing. Every fee model here charges on BOTH sides
 * — BIST brokerage is 0.195% in and 0.195% out, the transaction tax on metals and FX is 0.2%
 * either way — but only the monthly purchase path ever consulted a model. So an exit booked
 * proceeds at the quoted price, and the realized P&L was overstated by the exit commission on
 * every sale ever recorded.
 *
 * The fee is SUBTRACTED here, where it is added on a purchase. Both directions move the number
 * against the owner, which is the direction that gets noticed late.
 *
 * An unknown model leaves proceeds gross and says so. Guessing a broker from the asset type
 * would put a fabricated commission into realized P&L, which is worse than a stated gap.
 *
 * @returns {{qty:number, proceedsGross:number, proceedsNet:number, price:number,
 *            grossPrice:number, feeLocal:number|null, feeModel:string}}
 */
export function priceSale(h, qty, quotedPrice) {
  const q = Number(qty) || 0;
  const p = Number(quotedPrice) || 0;
  const gross = q * p;
  const { feeLocal, model } = feeFor(h, gross);
  const net = feeLocal == null ? gross : gross - feeLocal;
  return {
    qty: q,
    proceedsGross: gross,
    proceedsNet: net,
    price: q > 0 ? net / q : 0,
    grossPrice: p,
    feeLocal: feeLocal,
    feeModel: model,
  };
}

/**
 * Open trades that cannot be charged an exit commission because nothing records where they
 * are held. All eight of the owner's open positions are BIST equities with `broker: null`,
 * so the model resolves to unknown and their exits book gross proceeds — correct, but a gap
 * the watchdog has to show rather than absorb. (audit 2026-08-03)
 */
export function unknownFeeTrades(trades) {
  return (trades || [])
    .filter(t => t && !t._del && t.status !== 'Closed' && t.type !== 'TimeDeposit' && feeModelFor(t) === UNKNOWN)
    .map(t => ({ ticker: t.ticker, broker: t.broker || '(none)', type: t.type }));
}

/**
 * Holdings whose purchases are being recorded under a model nobody has verified.
 * Input to the watchdog — a fee that is unknown must look unknown.
 */
export function unknownFeeHoldings(holdings) {
  return (holdings || [])
    .filter(h => h && (h.qty || 0) > 0 && feeModelFor(h) === UNKNOWN)
    .map(h => ({ ticker: h.ticker, broker: h.broker || '(none)', type: h.type }));
}

/**
 * Total recorded fees over a set of monthly rate rows, in the entry's own currency,
 * grouped by ticker. Reports read this; nothing else does.
 */
export function feesByTicker(monthlyRates, months) {
  const out = {};
  const want = months && months.length ? new Set(months) : null;
  Object.entries(monthlyRates || {}).forEach(([m, r]) => {
    if (want && !want.has(m)) return;
    Object.entries((r && r.entries) || {}).forEach(([tk, e]) => {
      const f = e && Number(e.feeLocal);
      if (!(f > 0)) return;
      out[tk] = +((out[tk] || 0) + f).toFixed(6);
    });
  });
  return out;
}
